import { Col, Row } from "antd"; // Import Button from antd
import PubSub from "pubsub-js"; // Assuming PubSub is imported correctly
import { useEffect, useMemo, useState } from "react"; // Import React and useState
import { TimelineCard } from "../../components/timeline/TimelineCard";
import { TimelineD3Charts } from "../../components/timeline/TimelineD3Charts";
import { useToken } from "../../hooks/useToken";
import { TInterest } from "../../models/interests.model";
import { EPostStatus, EPostType, TPost } from "../../models/posts.model";
import { useTheme } from "../../providers/ThemeProvider"; // Import useTheme for messages
import { TResponse } from "../../services/apiWorker";
import { getInterests } from "../../services/interests.service";
import { getPosts } from "../../services/posts.service"; // createPost import removed if not used elsewhere
import {
  getEventsFromPublicTimeline,
  getFollowingPublicTimelines,
} from "../../services/ptimelines.service";
import { deepCopy } from "../../utils/deepCopy";

// Default content for a new post (Conteúdo inicial vazio)
const newPostInitialContent = JSON.stringify({
  root: {
    children: [
      {
        type: "paragraph",
        children: [
          {
            detail: 0,
            format: 0,
            mode: "normal",
            style: "",
            text: "", // Texto inicial vazio
            type: "text",
            version: 1,
          },
        ],
        direction: null,
        format: "",
        indent: 0,
        version: 1,
      },
    ],
    direction: null,
    format: "",
    indent: 0,
    type: "root",
    version: 1,
  },
});

export function Timeline() {
  const { token, screens } = useToken();
  const { message } = useTheme(); // Mantido para outras mensagens se necessário

  const [cardsPerColumn, setCardsPerColumn] = useState<{
    [key: number]: TPost[];
  }>({});
  const totalColumn = screens.xxl
    ? 6
    : screens.lg
    ? 4
    : screens.md
    ? 3
    : screens.sm
    ? 2
    : 1;

  const [posts, setPosts] = useState<TResponse<TPost>>(TResponse);
  const [interests, setInterests] = useState<TResponse<TInterest>>(TResponse);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);

  // --- State and Handlers for the "Create New Post" Modal ---
  // (Mantidos para abrir/fechar o modal)
  const [isNewPostModalOpen, setNewPostModalOpen] = useState<boolean>(false);

  const handleCloseNewPostModal = () => {
    setNewPostModalOpen(false);
  };

  useEffect(() => {
    // Fetch initial posts and interests (lógica mantida)
    getPosts({
      type: EPostType.EVENT,
      status: EPostStatus.PUBLISH,
      order: "desc",
      onSuccess: setPosts,
    });

    getFollowingPublicTimelines({
      onSuccess: (data) => {
        for (const timelineId of data.content) {
          getEventsFromPublicTimeline({
            timelineId,
            onSuccess: (eventData) => {
              setPosts((prevState) => {
                if (!prevState.content) return prevState;
                const existingIds = new Set(prevState.content.map((p) => p.id));
                const newContent = eventData.content.filter(
                  (p) => !existingIds.has(p.id)
                );
                if (newContent.length === 0) return prevState;

                const copy = deepCopy(prevState);
                copy.content = [
                  ...copy.content,
                  ...newContent.map((post) => {
                    return {
                      ...post,
                      eventDate: isNaN(new Date(post.eventDate || "").getTime())
                        ? null
                        : new Date(post.eventDate || "").toISOString(),
                    };
                  }),
                ];
                return copy;
              });
            },
          });
        }
      },
    });

    getInterests({
      onSuccess: setInterests,
    });

    // --- PubSub Listeners (lógica mantida) ---
    function onPostCreated(_: any, post: TPost) {
      console.log("PubSub: postCreated", post);
      setPosts((prevState) => {
        if (!prevState.content) return TResponse;
        const exists = prevState.content.some((p) => p.id === post.id);
        if (exists) return prevState;

        if (
          post.type == EPostType.EVENT &&
          post.status == EPostStatus.PUBLISH
        ) {
          const copy = deepCopy(prevState);
          copy.content.unshift(post);
          return copy;
        }
        return prevState;
      });
    }

    function onPostDeleted(_: any, _post: TPost) {
      console.log("PubSub: postDeleted", _post);
      setPosts((prevState) => {
        if (!prevState.content) return prevState;
        const copy = deepCopy(prevState);
        copy.content = copy.content.filter((post) => post.id != _post.id);
        return copy;
      });
    }

    function onPostUpdated(_: any, post: TPost) {
      console.log("PubSub: postUpdated", post);
      setPosts((prevState) => {
        if (!prevState.content) return prevState;
        const copy = deepCopy(prevState);
        const index = copy.content.findIndex((p) => p.id == post.id);
        if (index != -1) {
          copy.content[index] = post;
        } else if (
          post.type == EPostType.EVENT &&
          post.status == EPostStatus.PUBLISH
        ) {
          copy.content.unshift(post);
        }
        return copy;
      });
    }

    function onTimelineFollowed(_: any, timelineId: string) {
      console.log("PubSub: timelineFollowed", timelineId);
      getEventsFromPublicTimeline({
        timelineId,
        onSuccess: (data) => {
          setPosts((prevState) => {
            if (!prevState.content) return prevState;
            const existingIds = new Set(prevState.content.map((p) => p.id));
            const newContent = data.content.filter(
              (p) => !existingIds.has(p.id)
            );
            if (newContent.length === 0) return prevState;

            const copy = deepCopy(prevState);
            copy.content = [...copy.content, ...newContent];
            return copy;
          });
        },
      });
    }

    function onTimelineUnfollowed(_: any, timelineId: string) {
      console.log("PubSub: timelineUnfollowed", timelineId);
      setPosts((prevState) => {
        if (!prevState.content) return prevState;
        const copy = deepCopy(prevState);
        copy.content = copy.content.filter(
          (post) => post.metadata?.batchId !== timelineId
        );
        return copy;
      });
    }

    // Subscribe to events
    const tk1 = PubSub.subscribe("postCreated", onPostCreated);
    const tk2 = PubSub.subscribe("postDeleted", onPostDeleted);
    const tk3 = PubSub.subscribe("postUpdated", onPostUpdated);
    const tk4 = PubSub.subscribe("timelineFollowed", onTimelineFollowed);
    const tk5 = PubSub.subscribe("timelineUnfollowed", onTimelineUnfollowed);

    // Cleanup function to unsubscribe
    return () => {
      PubSub.unsubscribe(tk1);
      PubSub.unsubscribe(tk2);
      PubSub.unsubscribe(tk3);
      PubSub.unsubscribe(tk4);
      PubSub.unsubscribe(tk5);
    };
  }, []); // Empty dependency array ensures this runs only once on mount

  // Filter posts based on selected interests (lógica mantida)
  const filteredPosts = useMemo(() => {
    if (!posts.content) return [];

    if (!selectedCategoryIds || selectedCategoryIds.length === 0) {
      return posts.content.sort((a, b) => {
        const aDate = new Date(a.eventDate || "").getTime() || new Date(a.createdAt).getTime();
        const bDate = new Date(b.eventDate || "").getTime() || new Date(b.createdAt).getTime();

        return bDate - aDate;  
      });
    }

    return posts.content
      .filter((post) =>
        post.interests?.some((interestId) =>
          selectedCategoryIds.includes(interestId)
        )
      )
      .sort((a, b) => {
        const aDate = new Date(a.eventDate || "").getTime() || new Date(a.createdAt).getTime();
        const bDate = new Date(b.eventDate || "").getTime() || new Date(b.createdAt).getTime();

        return bDate - aDate;  
      });
  }, [JSON.stringify(posts.content), selectedCategoryIds]);

  // Distribute filtered posts into columns (lógica mantida)
  useEffect(() => {
    let cards: {
      [key: number]: TPost[];
    } = {};

    let i = 0;
    while (i < filteredPosts.length) {
      const columnIndex = i % totalColumn;
      if (!cards[columnIndex]) {
        cards[columnIndex] = [];
      }
      cards[columnIndex].push(filteredPosts[i]);
      i++;
    }

    setCardsPerColumn(cards);
  }, [JSON.stringify(filteredPosts), totalColumn]);

  // --- Render Component ---
  return (
    <>
      <div
        style={{
          padding: token.paddingLG,
          width: "100%",
          height: "calc(100vh - 64px)", // Example height, adjust as needed
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        <TimelineD3Charts
          interests={interests.content || []}
          posts={filteredPosts}
          selectedCategoryIds={selectedCategoryIds}
          setSelectedCategoryIds={setSelectedCategoryIds}
        />

        {/* Grid for Timeline Cards (Mantido) */}
        <Row style={{ marginTop: "20px" }} gutter={[24, 24]}>
          {Object.keys(cardsPerColumn).map((columnIndex) => {
            const colIndex = parseInt(columnIndex);
            if (
              !cardsPerColumn[colIndex] ||
              cardsPerColumn[colIndex].length === 0
            ) {
              return null;
            }
            return (
              <Col
                key={colIndex}
                span={24}
                sm={12}
                md={8}
                lg={6}
                xl={6}
                xxl={4}
              >
                {cardsPerColumn[colIndex].map((card) => {
                  if (!card || !card.id) return null;
                  return (
                    <div key={card.id} style={{ marginBottom: token.marginMD }}>
                      <TimelineCard
                        card={card}
                        interests={interests} // Pass the full interests response
                      />
                    </div>
                  );
                })}
              </Col>
            );
          })}
        </Row>
      </div>
    </>
  );
}
